import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './irlObservations2026.module.css';
import { OBS_STATUS_FILTERS } from './dummyData';
import {
    analyseObsUpdate,
    archiveObsRecord,
    bulkDeleteObsRecords,
    bulkImportObsRecords,
    bulkUpdateObsRecords,
    createObsFollowUpIrl,
    createObsRecord,
    deleteObsComment,
    dismissObsImpact,
    editObsComment,
    getObsScreen,
    hasObsProjectId,
    markObsDiscussionRead,
    openObsEvidence,
    postObsComment,
    reopenObsComment,
    resolveObsComment,
    resolveObsRecord,
    supersedeObsRecord,
    updateObsRecord,
    uploadObsEvidence,
} from '../../apiServices/obsScreenApi';
import {
    applyBulkDraftPatch,
    applyColumnMapping,
    autoMapCsvColumns,
    buildObsBulkImport,
    downloadCsv,
    downloadObservationTemplate,
    filledUpdates,
    formatBulkDeleteResult,
    formatImportResult,
    listExportFilenames,
    mergeObsImportRows,
    OBS_EXPORT_COLUMNS,
    OBS_IMPORT_FIELDS,
    parseCsvHeaders,
    parseCsvText,
    pruneSelection,
    splitObsImportWorkstreams,
    unmappedRequiredFields,
} from './listActions';
import { CsvColumnMapper, ObsImportWorkstreamModal } from './listChrome';
import { exportListPdf } from './listPdf';
import { GetIRLDocData } from '../../redux/documentsSlice';
import FileViewerModal from '../Dataroom2.0/FileViewerModal';
import { ModuleShell, ObsModuleHeader, ObsNavTree } from './chrome';
import { evidenceDocumentId, evidenceIncludesUploads, mentionIdsFromText, observationRagLabel, stripHtmlText, withPlainObservationText } from './screens/shared';
import ObsMain from './screens/Obs_Main';
import ObsOverview from './screens/Obs_Overview';
import ObsNewObs from './screens/Obs_New_Obs';
import ObsReviewIrl from './screens/Obs_Review_IRL';
import ObsReviewObs from './screens/Obs_Review_Obs';

const EVIDENCE_UPLOAD_POLL_MS = 2000;
const EVIDENCE_UPLOAD_WAIT_MS = 120000;
const EVIDENCE_STILL_PROCESSING = 'The file is still processing. You can stay on this tab.';

function looksLikeHtml(value) {
    if (typeof value !== 'string') return false;
    const text = value.trimStart().slice(0, 256).toLowerCase();
    return text.startsWith('<!doctype') || text.startsWith('<html') || text.includes('<body') || text.includes('<pre');
}

function errorText(error) {
    const data = error?.response?.data;
    if (!data) return 'The request could not be completed.';
    if (typeof data === 'string') {
        if (looksLikeHtml(data)) return 'The request could not be completed.';
        return data;
    }
    if (typeof data.detail === 'string') {
        if (looksLikeHtml(data.detail)) return 'The request could not be completed.';
        return data.detail;
    }
    const first = Object.values(data)[0];
    if (Array.isArray(first)) return String(first[0]);
    if (first && typeof first === 'object') return String(Object.values(first)[0]);
    if (typeof first === 'string') {
        if (looksLikeHtml(first)) return 'The request could not be completed.';
        return first;
    }
    return 'The request could not be completed.';
}

function sleep(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
}

function matchesObsStatus(item, status) {
    if (!status || status === 'All') return true;
    if (status === 'May need review') {
        return item.freshness === 'New evidence' || item.freshness === 'Review required' || item.freshness === 'Potentially affected';
    }
    if (status === 'New evidence' || status === 'Review required') {
        return item.freshness === status;
    }
    return item.status === status;
}

function filterObservations(items, { status, workstream, materiality, query }) {
    return items.filter(item => {
        if (!matchesObsStatus(item, status)) return false;
        if (workstream !== 'all' && String(item.workstreamId) !== String(workstream)) return false;
        if (materiality !== 'all' && item.materiality !== materiality) return false;
        if (query) {
            const haystack = `${item.id} ${stripHtmlText(item.title)}`.toLowerCase();
            if (!haystack.includes(query.toLowerCase())) return false;
        }
        return true;
    });
}

function emptyDraft(workstream = '') {
    return {
        title: '',
        body: '',
        workstream,
        materiality: 'High',
        rag: '',
        owner: '',
        related: '',
        relatedQuery: '',
        why: '',
    };
}

function emptyObsBulk() {
    return { owner: '', workstream: '', materiality: '' };
}

function obsExportRows(rows) {
    return (rows || []).map(item => ({
        id: item.id,
        title: stripHtmlText(item.title),
        body: stripHtmlText(item.body),
        why: stripHtmlText(item.why),
        workstream: item.workstream,
        status: item.status,
        materiality: item.materiality,
        owner: item.owner,
    }));
}

function relatedIrlDraft(item) {
    const related = item?.relatedIrls?.[0];
    const relatedId = related?.id || '';
    const relatedTitle = stripHtmlText(related?.title || '');
    return {
        related: relatedId,
        relatedQuery: relatedId ? [relatedId, relatedTitle].filter(Boolean).join(' ') : '',
    };
}

function draftFromObservation(item) {
    return {
        title: stripHtmlText(item?.title || ''),
        body: stripHtmlText(item?.body || ''),
        workstream: item?.workstreamId || '',
        materiality: item?.materiality || 'High',
        rag: observationRagLabel(item),
        owner: item?.ownerId || '',
        ...relatedIrlDraft(item),
        why: stripHtmlText(item?.why || ''),
    };
}

function applyItem(rows, updated) {
    if (!updated?.id) return rows;
    return rows.map(item => (item.id === updated.id || item.pk === updated.pk ? withPlainObservationText({ ...item, ...updated }) : item));
}

export default function ObsApp() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const { selectedProject } = useSelector(state => state.projectData);
    const [observations, setObservations] = React.useState([]);
    const [workstreams, setWorkstreams] = React.useState([]);
    const [members, setMembers] = React.useState([]);
    const [stats, setStats] = React.useState({ confirmed: 0, mayNeedReview: 0, draft: 0 });
    const [reviewAffected, setReviewAffected] = React.useState({ observations: [], irlResponses: [] });
    const [loadError, setLoadError] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [status, setStatus] = React.useState('All');
    const [workstream, setWorkstream] = React.useState('all');
    const [materiality, setMateriality] = React.useState('all');
    const [query, setQuery] = React.useState('');
    const [page, setPage] = React.useState('list');
    const [selectedId, setSelectedId] = React.useState(null);
    const [detailTab, setDetailTab] = React.useState('Overview');
    const [reviewTab, setReviewTab] = React.useState('IRL Responses');
    const [createOpen, setCreateOpen] = React.useState(false);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [bulkDraft, setBulkDraft] = React.useState(emptyObsBulk());
    const [listNotice, setListNotice] = React.useState('');
    const [csvImport, setCsvImport] = React.useState(null);
    const [workstreamAssign, setWorkstreamAssign] = React.useState(null);
    const [editingId, setEditingId] = React.useState(null);
    const [copied, setCopied] = React.useState(false);
    const [comment, setComment] = React.useState('');
    const [replyFor, setReplyFor] = React.useState(null);
    const [replyText, setReplyText] = React.useState('');
    const [copiedCommentId, setCopiedCommentId] = React.useState(null);
    const [focusCommentId, setFocusCommentId] = React.useState(null);
    const [draft, setDraft] = React.useState(emptyDraft());
    const [confirmAction, setConfirmAction] = React.useState(null);
    const [confirmNote, setConfirmNote] = React.useState('');
    const [supersedeId, setSupersedeId] = React.useState('');
    const [analysis, setAnalysis] = React.useState(null);
    const [uploadingEvidence, setUploadingEvidence] = React.useState(false);
    const [viewerOpen, setViewerOpen] = React.useState(false);
    const [viewerFile, setViewerFile] = React.useState(null);
    const [viewerWorkstream, setViewerWorkstream] = React.useState(null);
    const evidenceUploadWaitRef = React.useRef(0);

    const hasProject = hasObsProjectId();
    const selected = observations.find(item => item.id === selectedId);
    const filtered = filterObservations(observations, { status, workstream, materiality, query });
    const selectedRows = observations.filter(item => selectedIds.includes(item.id));
    const exportNames = listExportFilenames('Observations', selectedProject);
    const mentionCandidates = selected?.mentionCandidates?.length ? selected.mentionCandidates : members;

    const openDetail = React.useCallback((id, tab = 'Overview') => {
        setSelectedId(id);
        setDetailTab(tab);
        setPage('detail');
        setAnalysis(null);
    }, []);

    const applyScreen = React.useCallback(data => {
        const rows = (data.observations || []).map(withPlainObservationText);
        setObservations(rows);
        setWorkstreams(data.workstreams || []);
        setMembers(data.members || []);
        setStats(data.stats || { confirmed: 0, mayNeedReview: 0, draft: 0 });
        setReviewAffected(data.reviewAffected || { observations: [], irlResponses: [] });
        setLoadError('');
        return rows;
    }, []);

    const reload = React.useCallback(async () => {
        if (!hasObsProjectId()) return [];
        const data = await getObsScreen();
        return applyScreen(data);
    }, [applyScreen]);

    React.useEffect(() => {
        if (!hasObsProjectId()) {
            setObservations([]);
            setWorkstreams([]);
            setMembers([]);
            setStats({ confirmed: 0, mayNeedReview: 0, draft: 0 });
            setReviewAffected({ observations: [], irlResponses: [] });
            setLoadError('');
            return undefined;
        }
        let cancelled = false;
        getObsScreen()
            .then(data => {
                if (!cancelled) applyScreen(data);
            })
            .catch(error => {
                if (!cancelled) setLoadError(errorText(error));
            });
        return () => {
            cancelled = true;
        };
    }, [applyScreen]);

    React.useEffect(() => {
        setSelectedIds(current => pruneSelection(current, observations.map(item => item.id)));
    }, [observations]);

    React.useEffect(() => {
        const id = searchParams.get('observation') || searchParams.get('observationId');
        const commentId = searchParams.get('comment');
        if (id) {
            openDetail(id, commentId ? 'Discussion' : 'Overview');
            if (commentId) setFocusCommentId(commentId);
        }
    }, [searchParams, openDetail]);

    React.useEffect(() => {
        if (workstreams[0] && !draft.workstream) {
            setDraft(current => ({ ...current, workstream: current.workstream || String(workstreams[0].id) }));
        }
    }, [draft.workstream, workstreams]);

    React.useEffect(() => {
        if (detailTab === 'Discussion' && selected?.id && hasProject) {
            markObsDiscussionRead(selected.id).catch(() => {});
        }
    }, [detailTab, hasProject, selected?.id]);

    const applyUpdated = updated => {
        if (!updated) return;
        const plain = withPlainObservationText(updated);
        setObservations(current => applyItem(current, plain));
        if (updated.analysis) setAnalysis(updated.analysis);
        return plain;
    };

    const handleExportCsv = () => {
        if (!filtered.length) {
            setListNotice('Nothing to export.');
            return;
        }
        downloadCsv(obsExportRows(filtered), OBS_EXPORT_COLUMNS, exportNames.csv);
        setListNotice(`Exported ${filtered.length} observation${filtered.length === 1 ? '' : 's'} to CSV.`);
    };

    const handleDownloadTemplate = () => {
        downloadObservationTemplate(selectedProject);
    };

    const handleExportPdf = async () => {
        if (!filtered.length) {
            setListNotice('Nothing to export.');
            return;
        }
        try {
            await exportListPdf({
                rows: obsExportRows(filtered),
                columns: OBS_EXPORT_COLUMNS,
                title: 'Observations',
                projectName: selectedProject?.name || selectedProject?.project_name,
                filename: exportNames.pdf,
            });
            setListNotice(`Exported ${filtered.length} observation${filtered.length === 1 ? '' : 's'} to PDF.`);
        } catch (error) {
            setListNotice(error?.message || 'The PDF could not be exported.');
        }
    };

    const handleBulkApply = async () => {
        const values = filledUpdates(bulkDraft);
        if (!hasProject || !selectedRows.length || !Object.keys(values).length || busy) return;
        const updates = {};
        if (values.owner) updates.owner = values.owner;
        if (values.workstream) updates.workstream_id = values.workstream;
        if (values.materiality) updates.materiality = values.materiality;
        setBusy(true);
        try {
            const result = await bulkUpdateObsRecords({ ids: selectedRows.map(item => item.pk), updates });
            const updated = result?.updated || [];
            if (updated.length) {
                setObservations(current => {
                    const byPk = new Map(updated.map(item => [item.pk, withPlainObservationText(item)]));
                    return current.map(item => byPk.get(item.pk) || item);
                });
            } else {
                await reload();
            }
            setBulkDraft(emptyObsBulk());
            setListNotice(`Updated ${selectedRows.length} observation${selectedRows.length === 1 ? '' : 's'}.`);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleBulkDraft = patch => {
        setBulkDraft(current => applyBulkDraftPatch(current, patch));
    };

    const handleBulkDelete = async () => {
        if (!hasProject || !selectedRows.length || busy) return;
        setBusy(true);
        try {
            const result = await bulkDeleteObsRecords({ ids: selectedRows.map(item => item.pk) });
            const deleted = result?.deleted || [];
            const failed = result?.failed || [];
            setSelectedIds([]);
            setBulkDraft(emptyObsBulk());
            await reload();
            setListNotice(
                formatBulkDeleteResult(
                    { deleted: deleted.length || result?.count || 0, failed },
                    'observation',
                    'observations',
                ),
            );
        } catch (error) {
            setListNotice(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handlePickCsv = async file => {
        if (!hasProject || busy) return;
        try {
            const text = await file.text();
            const headers = parseCsvHeaders(text);
            if (!headers.length) {
                setListNotice('The CSV has no column headers.');
                return;
            }
            setWorkstreamAssign(null);
            setCsvImport({
                fileName: file.name,
                headers,
                rows: parseCsvText(text),
                mapping: autoMapCsvColumns(headers, OBS_IMPORT_FIELDS),
            });
        } catch (error) {
            setListNotice(error?.message || 'The CSV could not be read.');
        }
    };

    const runObsBulkImport = async mapped => {
        const { items, skipped } = buildObsBulkImport(mapped, { workstreams, members });
        setCsvImport(null);
        setWorkstreamAssign(null);
        setBusy(true);
        try {
            const result = await bulkImportObsRecords({ rows: items });
            if (result?.observations) {
                applyScreen(result);
            } else {
                await reload();
            }
            setListNotice(
                formatImportResult({
                    created: result?.created || 0,
                    updated: result?.updated || 0,
                    skipped: [...skipped, ...(result?.skipped || [])],
                }),
            );
        } catch (error) {
            setListNotice(errorText(error) || 'The CSV could not be imported.');
        } finally {
            setBusy(false);
        }
    };

    const handleConfirmCsvImport = async () => {
        if (!hasProject || !csvImport || busy) return;
        if (unmappedRequiredFields(csvImport.mapping, OBS_IMPORT_FIELDS).length) return;
        const mapped = applyColumnMapping(csvImport.rows, csvImport.mapping, OBS_IMPORT_FIELDS);
        const { matched, needsAssignment } = splitObsImportWorkstreams(mapped, workstreams);
        if (needsAssignment.length) {
            setCsvImport(null);
            setWorkstreamAssign({ matched, needsAssignment });
            return;
        }
        await runObsBulkImport(mapped);
    };

    const handleConfirmWorkstreamAssign = async () => {
        if (!hasProject || !workstreamAssign || busy) return;
        if (workstreamAssign.needsAssignment.some(row => !String(row.workstreamId || '').trim())) return;
        await runObsBulkImport(mergeObsImportRows(workstreamAssign.matched, workstreamAssign.needsAssignment));
    };

    const handleCreate = async nextStatus => {
        if (!draft.title.trim() || !hasProject || busy) return;
        setBusy(true);
        try {
            const body = {
                title: draft.title,
                body: draft.body,
                why: draft.why,
                workstream_id: draft.workstream,
                materiality: draft.materiality,
                rag: draft.rag || '',
                owner: draft.owner || undefined,
                related: draft.related || undefined,
                status: nextStatus || 'Confirmed',
            };
            const saved = editingId ? await updateObsRecord(editingId, body) : await createObsRecord(body);
            await reload();
            setCreateOpen(false);
            setEditingId(null);
            setDraft(emptyDraft(workstreams[0] ? String(workstreams[0].id) : ''));
            if (saved?.id) openDetail(saved.id);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleCopyLink = () => {
        if (!selectedId) return;
        const url = `${window.location.origin}/observations?observation=${selectedId}`;
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };

    const openCreate = () => {
        setEditingId(null);
        setDraft(emptyDraft(workstreams[0] ? String(workstreams[0].id) : ''));
        setCreateOpen(true);
    };

    const openEdit = () => {
        if (!selected) return;
        setEditingId(selected.id);
        setDraft(draftFromObservation(selected));
        setCreateOpen(true);
    };

    const runWithItem = async fn => {
        if (!hasProject || !selected?.id || busy) return false;
        setBusy(true);
        try {
            const updated = await fn(selected.id);
            applyUpdated(updated);
            await reload();
            return updated;
        } catch (error) {
            setLoadError(errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const postComment = async parentId => {
        const body = parentId ? replyText : comment;
        if (!hasProject || !body.trim() || !selected?.id || busy) return;
        const mentionIds = mentionIdsFromText(body, mentionCandidates);
        setBusy(true);
        try {
            const updated = await postObsComment(selected.id, body, parentId || undefined, mentionIds);
            applyUpdated(updated);
            setComment('');
            setReplyText('');
            setReplyFor(null);
            await reload();
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleEditComment = async (item, next, mentionIds) => {
        const text = String(next || '').trim();
        if (!text || !hasProject || !selected?.id) return false;
        setBusy(true);
        try {
            const updated = await editObsComment(selected.id, item.id, next, mentionIds || mentionIdsFromText(next, mentionCandidates));
            applyUpdated(updated);
            await reload();
            return true;
        } catch (error) {
            setLoadError(errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteComment = async item => {
        if (!item?.id || !hasProject || !selected?.id || busy) return false;
        setBusy(true);
        try {
            const updated = await deleteObsComment(selected.id, item.id);
            applyUpdated(updated);
            await reload();
            return true;
        } catch (error) {
            setLoadError(errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleCopyCommentLink = item => {
        const url = `${window.location.origin}/observations?observation=${selectedId}&comment=${item.id}`;
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
        setCopiedCommentId(item.id);
        window.setTimeout(() => setCopiedCommentId(null), 1500);
    };

    const handleAnalyse = async id => {
        const target = id || selectedId;
        if (!hasProject || !target || busy) return;
        setBusy(true);
        try {
            const updated = await analyseObsUpdate(target);
            applyUpdated(updated);
            setAnalysis(updated.analysis || null);
            openDetail(target, 'Overview');
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleDismiss = async id => {
        const target = id || selectedId;
        if (!hasProject || !target || busy) return;
        setBusy(true);
        try {
            await dismissObsImpact(target);
            await reload();
            setAnalysis(null);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleConfirmAction = async () => {
        if (!selected?.id || busy) return;
        setBusy(true);
        try {
            if (confirmAction === 'resolve') {
                await resolveObsRecord(selected.id, confirmNote);
            } else if (confirmAction === 'archive') {
                await archiveObsRecord(selected.id);
            } else if (confirmAction === 'supersede') {
                await supersedeObsRecord(selected.id, supersedeId, confirmNote);
            } else if (confirmAction === 'followUp') {
                const created = await createObsFollowUpIrl(selected.id, {
                    question: confirmNote || selected.title,
                    workstream_id: selected.workstreamId,
                    reason: selected.why,
                });
                setConfirmAction(null);
                if (created?.followUpIrl?.id) {
                    navigate(`/irl?request=${created.followUpIrl.id}`);
                    return;
                }
            }
            await reload();
            setConfirmAction(null);
            setConfirmNote('');
            setSupersedeId('');
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleAcceptUpdate = async () => {
        if (!analysis || !selected?.id) return;
        await runWithItem(id =>
            updateObsRecord(id, {
                title: analysis.proposed_title || selected.title,
                body: analysis.proposed_observation || selected.body,
                why: analysis.proposed_why_it_matters || selected.why,
                materiality: analysis.proposed_materiality || selected.materiality,
                status: 'Confirmed',
            })
        );
        setAnalysis(null);
    };

    const handleUploadEvidence = async files => {
        if (!hasProject || !selected?.id || !files?.length || busy || uploadingEvidence) return;
        const observationId = selected.id;
        const pendingFiles = Array.from(files);
        const pendingNames = pendingFiles.map(file => file.name);
        const beforeIds = new Set((selected.evidence || []).map(evidenceDocumentId).filter(Boolean));
        const beforeCount = (selected.evidence || []).length;
        const waitId = evidenceUploadWaitRef.current + 1;
        evidenceUploadWaitRef.current = waitId;
        const uploadVisible = item => evidenceIncludesUploads(item, { beforeIds, beforeCount, pendingNames });
        setLoadError('');
        setUploadingEvidence(true);
        setBusy(true);
        setPage('detail');
        setDetailTab('Evidence');
        try {
            const updated = await uploadObsEvidence(observationId, pendingFiles);
            if (waitId !== evidenceUploadWaitRef.current) return;
            applyUpdated(updated);
            if (uploadVisible(updated)) return;
            const deadline = Date.now() + EVIDENCE_UPLOAD_WAIT_MS;
            while (Date.now() < deadline) {
                setBusy(false);
                await sleep(EVIDENCE_UPLOAD_POLL_MS);
                if (waitId !== evidenceUploadWaitRef.current) return;
                const data = await getObsScreen();
                if (waitId !== evidenceUploadWaitRef.current) return;
                const rows = applyScreen(data);
                const match = rows.find(item => item.id === observationId);
                if (uploadVisible(match)) return;
            }
            setLoadError(EVIDENCE_STILL_PROCESSING);
        } catch (error) {
            if (waitId === evidenceUploadWaitRef.current) setLoadError(errorText(error));
        } finally {
            if (waitId === evidenceUploadWaitRef.current) {
                setUploadingEvidence(false);
                setBusy(false);
            }
        }
    };

    const openViewer = (file, workstreamForFile) => {
        setViewerWorkstream(workstreamForFile || null);
        setViewerFile(file);
        setViewerOpen(true);
    };

    const handleOpenFile = async file => {
        const documentId = evidenceDocumentId(file);
        const workstreamId = file?.workstreamId ?? file?.workstream_id;
        try {
            setLoadError('');
            if (documentId && workstreamId) {
                const res = await dispatch(GetIRLDocData({ id: documentId, workstream: workstreamId }));
                if (res?.type !== 'IRLDOC/getIRLDocData/fulfilled') {
                    setLoadError('This file cannot be previewed.');
                    return;
                }
                const doc = res.payload?.data;
                if (!doc?.document && !doc?.document_link) {
                    setLoadError('This file cannot be previewed.');
                    return;
                }
                const ws =
                    workstreams.find(item => String(item.id) === String(workstreamId)) || {
                        id: workstreamId,
                    };
                openViewer(
                    {
                        ...doc,
                        filePath: file.path || [ws.name, doc.ws_folder].filter(Boolean).join('/'),
                    },
                    ws,
                );
                return;
            }
            if (!file?.openUrl) return;
            const data = await openObsEvidence(file.openUrl);
            if (!data?.url) return;
            openViewer({
                id: documentId || undefined,
                document_name: file.name || data.filename,
                document: data.url,
                filePath: file.path || '',
            });
        } catch (error) {
            setLoadError(errorText(error));
        }
    };

    return (
        <>
        <ModuleShell>
            <ObsModuleHeader stats={stats} />
            <div className={styles.body}>
                <ObsNavTree
                    status={status}
                    workstream={workstream}
                    observations={observations}
                    workstreams={workstreams}
                    onStatus={value => {
                        setStatus(value);
                        setPage('list');
                    }}
                    onWorkstream={value => {
                        setWorkstream(value);
                        setPage('list');
                    }}
                />
                <div className={styles.main}>
                    <div className={styles.mainScroll}>
                        {loadError ? <p className={styles.emptyBody}>{loadError}</p> : null}
                        {!hasProject ? <p className={styles.emptyBody}>Select a project to load observations.</p> : null}
                        {page === 'list' && (
                            <ObsMain
                                observations={filtered}
                                status={status}
                                onStatus={setStatus}
                                workstream={workstream}
                                onWorkstream={setWorkstream}
                                materiality={materiality}
                                onMateriality={setMateriality}
                                query={query}
                                onQuery={setQuery}
                                onOpen={openDetail}
                                onCreate={openCreate}
                                onReview={() => {
                                    setPage('review');
                                    setReviewTab('IRL Responses');
                                }}
                                workstreams={workstreams}
                                members={members}
                                stats={stats}
                                statusFilters={OBS_STATUS_FILTERS}
                                menuOpen={menuOpen}
                                onMenu={setMenuOpen}
                                selectedIds={selectedIds}
                                onSelectedIds={setSelectedIds}
                                bulkDraft={bulkDraft}
                                onBulkDraft={handleBulkDraft}
                                onBulkApply={handleBulkApply}
                                onBulkDelete={handleBulkDelete}
                                onExportCsv={handleExportCsv}
                                onExportPdf={handleExportPdf}
                                onDownloadTemplate={handleDownloadTemplate}
                                onImport={handlePickCsv}
                                notice={listNotice}
                                busy={busy}
                            />
                        )}
                        {page === 'detail' && selected && (
                            <ObsOverview
                                observation={selected}
                                tab={detailTab}
                                onTab={setDetailTab}
                                onBack={() => setPage('list')}
                                onCopy={handleCopyLink}
                                copied={copied}
                                onRelatedIrl={id => navigate(`/irl?request=${id}`)}
                                onEdit={openEdit}
                                comment={comment}
                                onComment={setComment}
                                onPostComment={() => postComment()}
                                mentionCandidates={mentionCandidates}
                                onReply={setReplyFor}
                                replyFor={replyFor}
                                replyText={replyText}
                                onReplyText={setReplyText}
                                onSubmitReply={() => postComment(replyFor)}
                                onEditComment={handleEditComment}
                                onDeleteComment={handleDeleteComment}
                                onCopyComment={handleCopyCommentLink}
                                copiedCommentId={copiedCommentId}
                                onResolveThread={item => runWithItem(id => resolveObsComment(id, item.id))}
                                onReopenThread={item => runWithItem(id => reopenObsComment(id, item.id))}
                                focusCommentId={focusCommentId}
                                onResolve={() => setConfirmAction('resolve')}
                                onSupersede={() => setConfirmAction('supersede')}
                                onArchive={() => setConfirmAction('archive')}
                                onFollowUp={() => {
                                    setConfirmNote(selected.title);
                                    setConfirmAction('followUp');
                                }}
                                onAnalyse={() => handleAnalyse(selected.id)}
                                onDismissImpact={() => handleDismiss(selected.id)}
                                onOpenFile={handleOpenFile}
                                onUpload={handleUploadEvidence}
                                uploading={uploadingEvidence}
                                analysis={analysis}
                                onAcceptUpdate={handleAcceptUpdate}
                                onLeaveUnchanged={() => setAnalysis(null)}
                                busy={busy}
                            />
                        )}
                        {page === 'detail' && hasProject && !selected && (
                            <p className={styles.emptyBody}>That observation was not found.</p>
                        )}
                        {page === 'review' && reviewTab === 'IRL Responses' && (
                            <ObsReviewIrl
                                items={reviewAffected.irlResponses || []}
                                obsCount={(reviewAffected.observations || []).length}
                                tab={reviewTab}
                                onTab={setReviewTab}
                                onBack={() => setPage('list')}
                                onOpenIrl={id => navigate(`/irl?request=${id}`)}
                            />
                        )}
                        {page === 'review' && reviewTab === 'Observations' && (
                            <ObsReviewObs
                                items={reviewAffected.observations || []}
                                irlCount={(reviewAffected.irlResponses || []).length}
                                tab={reviewTab}
                                onTab={setReviewTab}
                                onBack={() => setPage('list')}
                                onOpenObs={openDetail}
                                onAnalyse={handleAnalyse}
                                onDismiss={handleDismiss}
                                busy={busy}
                            />
                        )}
                    </div>
                </div>
            </div>
            {csvImport ? (
                <CsvColumnMapper
                    fileName={csvImport.fileName}
                    headers={csvImport.headers}
                    fields={OBS_IMPORT_FIELDS}
                    mapping={csvImport.mapping}
                    onChange={mapping => setCsvImport(current => (current ? { ...current, mapping } : current))}
                    onCancel={() => setCsvImport(null)}
                    onConfirm={handleConfirmCsvImport}
                    busy={busy}
                    entityLabel="observation"
                />
            ) : null}
            {workstreamAssign ? (
                <ObsImportWorkstreamModal
                    rows={workstreamAssign.needsAssignment}
                    matchedCount={workstreamAssign.matched.length}
                    workstreams={workstreams}
                    onChangeRows={needsAssignment =>
                        setWorkstreamAssign(current => (current ? { ...current, needsAssignment } : current))
                    }
                    onCancel={() => setWorkstreamAssign(null)}
                    onConfirm={handleConfirmWorkstreamAssign}
                    busy={busy}
                />
            ) : null}
            {createOpen && (
                <ObsNewObs
                    draft={draft}
                    onChange={setDraft}
                    onCreate={handleCreate}
                    onCancel={() => {
                        setCreateOpen(false);
                        setEditingId(null);
                    }}
                    workstreams={workstreams}
                    members={members}
                    editing={Boolean(editingId)}
                    busy={busy}
                />
            )}
            {confirmAction && (
                <div className={styles.overlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalHead}>
                            <h2 className={styles.modalTitle}>
                                {confirmAction === 'resolve'
                                    ? 'Resolve Observation?'
                                    : confirmAction === 'archive'
                                      ? 'Archive Observation?'
                                      : confirmAction === 'supersede'
                                        ? 'Supersede Observation?'
                                        : 'Create follow-up IRL'}
                            </h2>
                        </div>
                        <div className={styles.modalBody}>
                            {confirmAction === 'resolve' ? (
                                <p className={styles.modalIntro}>This marks the current Observation as resolved. Its full history and evidence remain available.</p>
                            ) : null}
                            {confirmAction === 'archive' ? (
                                <p className={styles.modalIntro}>Archive is organisational, not evidentiary deletion. History remains available.</p>
                            ) : null}
                            {confirmAction === 'supersede' ? (
                                <>
                                    <p className={styles.modalIntro}>Choose the replacement Observation. The historical record remains accessible.</p>
                                    <label className={styles.fieldLabel}>Replacement observation</label>
                                    <select className={styles.selectField} value={supersedeId} onChange={event => setSupersedeId(event.target.value)}>
                                        <option value="">Select observation</option>
                                        {observations
                                            .filter(item => item.id !== selectedId)
                                            .map(item => (
                                                <option key={item.id} value={item.id}>
                                                    {item.id} · {stripHtmlText(item.title)}
                                                </option>
                                            ))}
                                    </select>
                                </>
                            ) : null}
                            {confirmAction === 'followUp' ? (
                                <p className={styles.modalIntro}>A follow-up IRL will be created and linked to this Observation.</p>
                            ) : null}
                            <label className={styles.fieldLabel}>{confirmAction === 'followUp' ? 'Question' : 'Note'}</label>
                            <textarea className={styles.textarea} value={confirmNote} onChange={event => setConfirmNote(event.target.value)} />
                        </div>
                        <div className={styles.modalFoot}>
                            <button type="button" className={styles.mutedBtn} onClick={() => setConfirmAction(null)} disabled={busy}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                onClick={handleConfirmAction}
                                disabled={busy || (confirmAction === 'supersede' && !supersedeId)}>
                                {confirmAction === 'resolve'
                                    ? 'Resolve Observation'
                                    : confirmAction === 'archive'
                                      ? 'Archive'
                                      : confirmAction === 'supersede'
                                        ? 'Supersede'
                                        : 'Create IRL'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ModuleShell>
        <FileViewerModal
            openModal={viewerOpen}
            setOpenModal={setViewerOpen}
            selectedFile={viewerFile}
            setSelectedFile={setViewerFile}
            folderFiles={viewerFile ? [viewerFile] : []}
            selectedFileIndex={0}
            workstream={viewerWorkstream}
        />
        </>
    );
}
